import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/glitch-ui/spinner';
import { CheckCircle, ExternalLink, AlertCircle, Info } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getMetaOAuthUrl, disconnectMetaIntegration, listMetaAdAccounts } from '@/lib/apis';
import { useToast } from '@/hooks/useToast';
import { withMetaReturnPath } from '@/lib/meta';
import type { MetaIntegrationStatus } from '@/lib/types';
import { usePostHog } from 'posthog-js/react';

interface MetaIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  integrationStatus: MetaIntegrationStatus | null;
}

export function MetaIntegrationModal({ isOpen, onClose, integrationStatus }: MetaIntegrationModalProps) {
  const posthog = usePostHog();
  const [isConnecting, setIsConnecting] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const connectMutation = useMutation({
    mutationFn: getMetaOAuthUrl,
    onSuccess: (data) => {
      // Funnel START only — the OAuth URL was just minted / the redirect is kicking off; the user
      // hasn't authorized yet. The real completion fires 'meta_connected' in the OAuth callback;
      // capturing it here overcounted the connect funnel (#6163 P1-11).
      posthog?.capture('meta_connect_started');
      const oauthUrl = new URL(data.oauth_url);
      const state = oauthUrl.searchParams.get('state');
      if (state) {
        // Ride the page the user connected from on the opaque CSRF state (#5907) so the OAuth
        // callback can land them back where they started — Meta's redirect flow returning to
        // origin like Google's popup flow. The backend strips the suffix before comparing the
        // nonce (oauth_state.core_nonce).
        // pathname + hash: the picker mirrors its active platform tab into the hash, so the
        // round trip restores the tab the user connected from (not just the page).
        let carried = withMetaReturnPath(state, window.location.pathname + window.location.hash);
        // Local dev: a Live-mode Meta app refuses http://localhost redirect URIs, so local
        // META_REDIRECT_URI points at the staging-hosted relay page (public/meta-oauth-relay.html),
        // registered ONCE for the whole team. Ride this workspace's port too, LAST — the relay's
        // matcher is anchored to the end of the state. Staging/prod hostnames leave the URL
        // untouched — their redirect URIs point straight at the app.
        if (window.location.hostname === 'localhost') {
          carried = `${carried}|p${window.location.port}`;
        }
        oauthUrl.searchParams.set('state', carried);
      }
      // Redirect to the Meta (Facebook) OAuth dialog
      window.location.href = oauthUrl.toString();
    },
    onError: (error) => {
      setIsConnecting(false);
      toast({
        title: 'Connection Failed',
        description: error.message || 'Failed to initiate Meta connection',
        variant: 'destructive',
      });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectMetaIntegration,
    onSuccess: (data) => {
      // success=false rides a 200 (e.g. a stale tab disconnecting an already-disconnected
      // integration), so it must be handled here — a throw inside onSuccess escapes React
      // Query's onError and crashes the component. Invalidate anyway so the stale UI resyncs.
      if (!data.success) {
        toast({ title: 'Disconnection Failed', description: data.message, variant: 'destructive' });
        queryClient.invalidateQueries({ queryKey: ['meta', 'integration', 'status'] });
        return;
      }
      posthog?.capture('meta_disconnected');
      queryClient.invalidateQueries({ queryKey: ['meta', 'integration', 'status'] });
      onClose();
    },
    onError: (error) => {
      toast({
        title: 'Disconnection Failed',
        description: error.message || 'Failed to disconnect Meta integration',
        variant: 'destructive',
      });
    },
  });

  // Refresh Connection — mirrors HubSpot's ``refreshHubSpotTokens`` action. For Meta, "refresh" means
  // re-syncing the ad-account list from the Graph API (``GET /meta/ad-accounts?sync=true``); the OAuth
  // token doesn't need explicit refresh from the user's side, but ad-account membership can drift on
  // the Meta side (new accounts added, accounts removed) and this is the user's escape hatch. #4173.
  const refreshAdAccountsMutation = useMutation({
    mutationFn: () => listMetaAdAccounts(true),
    onSuccess: () => {
      posthog?.capture('meta_ad_accounts_refreshed');
      // No success toast (house rule): the re-synced account list is the visible confirmation.
      queryClient.invalidateQueries({ queryKey: ['meta', 'integration', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['meta', 'ad-accounts'] });
    },
    onError: (error) => {
      toast({
        title: 'Refresh Failed',
        description: error.message || 'Failed to refresh Meta connection',
        variant: 'destructive',
      });
    },
  });

  const handleConnect = () => {
    setIsConnecting(true);
    connectMutation.mutate();
  };

  const handleDisconnect = () => {
    disconnectMutation.mutate();
  };

  const handleRefresh = () => {
    refreshAdAccountsMutation.mutate();
  };

  const isConnected = integrationStatus?.is_connected || false;
  const isLoading = connectMutation.isPending || disconnectMutation.isPending || refreshAdAccountsMutation.isPending;
  const adAccountCount = integrationStatus?.ad_accounts?.length ?? 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <img src="/meta.png" alt="Meta" className="w-6 h-auto" />
            Meta Ads Integration
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Connection Status */}
          <div className="flex items-start gap-3 p-4 border rounded-lg">
            {isConnected ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-green-900">Connected</p>
                  <p className="text-sm text-gray-700">{integrationStatus?.name || 'Meta Business Account'}</p>
                  <p className="text-xs text-gray-500">
                    {adAccountCount === 1 ? '1 ad account' : `${adAccountCount} ad accounts`}
                  </p>
                  {integrationStatus?.last_sync_at && (
                    <p className="text-xs text-gray-500">
                      Last sync: {new Date(integrationStatus.last_sync_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </>
            ) : integrationStatus?.connection_broken ? (
              // #5949: the platform killed the connection (revoked token / app removed) —
              // say so instead of presenting like a first-time connect.
              <>
                <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Connection Lost</p>
                  <p className="text-sm text-gray-500">
                    The Meta connection has expired or was revoked. Reconnect to resume Meta campaigns and
                    reporting.
                  </p>
                </div>
              </>
            ) : (
              <>
                <Info className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Not Connected</p>
                  <p className="text-sm text-gray-500">
                    Connect a Meta Business account to manage Facebook and Instagram ad campaigns alongside Google
                    Ads.
                  </p>
                </div>
              </>
            )}
          </div>

          {/*
            Ad-account picker removed (#4173 Chunk 11): selection is forced at OAuth-completion time
            via the auto-opened modal on the integrations page (see ``IntegrationSettings``);
            switching accounts later happens via the user-menu's "Ad Accounts → Meta" entry
            (``MetaAdAccountSelectionModal``) and the org-overview Switch (#5926). Both the Meta and HubSpot
            integration modals (#5936) own the
            connection lifecycle only (Google has no modal since #6668; its card owns it) — ad-account switching for both platforms lives in the
            user-menu "Ad Accounts" section. This modal is structurally identical to HubSpot's.
            "Go to Meta campaigns" link also removed in Chunk 5 — Meta campaigns live in the unified
            ``/campaigns`` surface now.
          */}

          {isConnected && integrationStatus?.scopes && integrationStatus.scopes.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-900">Granted Permissions:</h4>
              {/*
                Single blue blob — HubSpot renders its scopes the same way (space-separated inside one
                chip). Meta's API returns scopes as separate array entries, but for visual parity with
                HubSpot per #4173, we join and render as one chip.
              */}
              <div className="flex flex-wrap gap-1">
                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-md">
                  {integrationStatus.scopes.join(' ')}
                </span>
              </div>
            </div>
          )}

          {/* A failed connect surfaces via the onError toast (server error → toast, house rule);
              the inline shadcn Alert here double-surfaced it, so it's gone (#6163 P2). */}

          {/* Actions — mirrors HubSpot's pattern: Refresh Connection (outline) above Disconnect (red). */}
          <div className="flex flex-col gap-3">
            {!isConnected ? (
              <Button onClick={handleConnect} disabled={isLoading || isConnecting} className="w-full">
                {isLoading || isConnecting ? (
                  <>
                    <Spinner className="w-4 h-4 mr-2" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {integrationStatus?.connection_broken ? 'Reconnect Meta Account' : 'Connect Meta Account'}
                  </>
                )}
              </Button>
            ) : (
              <div className="space-y-2">
                <Button
                  onClick={handleRefresh}
                  disabled={isLoading}
                  variant="outline"
                  className="w-full"
                >
                  {refreshAdAccountsMutation.isPending ? (
                    <>
                      <Spinner className="w-4 h-4 mr-2" />
                      Refreshing...
                    </>
                  ) : (
                    'Refresh Connection'
                  )}
                </Button>

                <Button
                  onClick={handleDisconnect}
                  disabled={isLoading}
                  variant="destructive"
                  className="w-full"
                >
                  {disconnectMutation.isPending ? (
                    <>
                      <Spinner className="w-4 h-4 mr-2" />
                      Disconnecting...
                    </>
                  ) : (
                    'Disconnect Meta'
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Help Text */}
          <div className="text-xs text-gray-500 space-y-1">
            <p>• Meta credentials are encrypted and stored securely</p>
            <p>• The connection can be removed at any time from this settings page</p>
            <p>• Only the permissions granted will be accessible</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
